import { useEffect, useState, useContext } from "react";
import { BASELINE } from "../../util";
import axios from "axios";
import { Doughnut } from 'react-chartjs-2';
import { UserContext } from "../../context/UserContext";
import { motion } from "framer-motion";
import { useParams, useLocation, useNavigate } from "react-router-dom";

const ACTScoreboard = () => {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [testData, setTestData] = useState({});
    const [error, setError] = useState("");
    const [scores, setScores] = useState({});
    const [missingTopics, setmissingTopics] = useState({});

    const [topics, settopics] = useState([
        { topic: "POW", full: "Production of Wirting" },
        { topic: "KLA", full: "knowledge of Language" },
        { topic: "CSE", full: "Conventions of Standard English" },

        { topic: "IES", full: "Intergrating Essential Skills" },
        { topic: "S", full: "Statistics and Probability" },
        { topic: "A", full: "Algebra" },
        { topic: "G", full: "Geometry" },
        { topic: "F", full: "Functions" },
        { topic: "N", full: "Number and Quantity" },
        { topic: "T", full: "Trigonometry" },

        { topic: "CS", full: "Craft and Structure" },
        { topic: "KID", full: "Key Ideas and Details" },
        { topic: "IKI", full: "Integration of Knowledge and Ideas" },

        { topic: "IOD", full: "Interpretation of Data" },
        { topic: "SIN", full: "Scientific Investigation" },
        { topic: "EMI", full: "Evaluation Models, Inferences, and Exprimental Results" }
    ]);

    const location = useLocation();
    const data = location.state?.data;

    const subjects = ['English', 'Math', 'Reading', 'Science'];
    const getChartData = (score) => ({
        labels: ['Score', 'Remaining'],
        datasets: [
            {
                data: [score, 36 - score],
                backgroundColor: ['#7aa0ff', '#2D3748'], // Changed to match dark theme blue accent
                borderWidth: 0,
            },
        ],
    });

    const params = useParams();

    useEffect(() => {
        if (!data) {
            getTestData(params.id);
        } else {
            setTestData(data);
        }
    }, [params.id, data, user, navigate]);

    const expandTopicNames = (groupedTopics, topics) => {
        const fullNames = {};

        const topicMapping = topics.reduce((acc, topic) => {
            acc[topic.topic] = topic.full;
            return acc;
        }, {});

        Object.keys(groupedTopics).forEach(subject => {
            fullNames[subject] = groupedTopics[subject].map(abbr => {
                const cleanAbbr = abbr.replace(/[\[\]"]+/g, '');
                return topicMapping[cleanAbbr] || cleanAbbr;
            });
        });

        return fullNames;
    };

    async function getTestData(id) {
        try {
            let res = await axios.get(BASELINE + "tests/" + id);
            setTestData(res.data);

            const parsedScores = JSON.parse(res.data.eng_score);
            setScores(parsedScores);

            const wrongquestions = JSON.parse(res.data.Incorrect_questionIds);
            const groupedTopics = {};

            wrongquestions.forEach((question) => {
                const section = question.section;
                const category = question.category;

                if (!groupedTopics[section]) {
                    groupedTopics[section] = [];
                }

                if (!groupedTopics[section].includes(category)) {
                    groupedTopics[section].push(category);
                }
            });

            const expandedNames = expandTopicNames(groupedTopics, topics);
            setmissingTopics(expandedNames);

        } catch (err) {
            setError(err.message)
        }
    }

    return (
        <div className="flex pt-2 justify-center items-center flex-col w-full h-full bg-[#1a1a1a]">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="pl-6 pr-6 w-full max-w-[1000px] 2xl:max-w-[1300px] border border-gray-700/30 bg-gradient-to-b from-[#2a2a2a]/70 to-[#262626]/50 p-8 rounded-xl shadow-xl"
            >
                <h1 className="text-[24px] sm:text-[32px] md:text-[40px] font-bold text-white">
                    {user && (<>
                        <span className="text-[#7aa0ff]">{user.first_name}</span>'s ACT Analytics Report is Here!
                    </>)}
                </h1>
                <h2 className="text-[20px] sm:text-[24px] md:text-[28px] font-bold text-white">
                    Uncover Your Insights Through MMT Prep.
                </h2>
                <p className="text-gray-300 mt-[5px]">
                    Our AI-powered test accurately predicts your score and provides a detailed analysis of your strengths and weaknesses based on the questions you've answered.
                </p>
                
                <div className="mt-8">
                    <div className="flex items-center mb-4">
                        <div className="w-1 h-5 bg-[#5c8dff] rounded-full mr-2"></div>
                        <h3 className="text-[18px] sm:text-[22px] md:text-[26px] font-bold text-white">
                            Score Analysis
                        </h3>
                    </div>
                    
                    <div className="mt-2 bg-[#2d2d2d] p-6 rounded-xl shadow-md flex flex-col">
                        <div className="flex flex-wrap justify-between mb-8">
                            {subjects.map((subject, index) => (
                                <div key={index} className="text-center mt-4 px-2">
                                    <Doughnut
                                        data={getChartData(scores[subject.toLowerCase()])}
                                        options={{
                                            cutout: '80%',
                                            plugins: {
                                                tooltip: { enabled: false },
                                                legend: { display: false },
                                            },
                                        }}
                                        className="w-24 h-24 mx-auto"
                                    />
                                    <div className="mt-2 text-lg">
                                        <p className="text-[#8cb0ff] inline-block">{subject}:</p>
                                        <p className="inline-block text-white font-bold ml-1">
                                            {scores[subject.toLowerCase()]} /36
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                
                {/* Topic Boxes */}
                <div className="mt-8">
                    <div className="flex items-center mb-4">
                        <div className="w-1 h-5 bg-[#5c8dff] rounded-full mr-2"></div>
                        <h3 className="text-[18px] sm:text-[22px] font-bold text-white">
                            Missing Topics
                        </h3>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <GradientBorderBox
                            title="English"
                            score={scores.english}
                            range="0 - 36"
                            description="Topics to Focus On"
                            icon="🗣️"
                            topics={missingTopics.english}
                        />

                        <GradientBorderBox
                            title="Math"
                            score={scores.math}
                            range="0 - 36"
                            description="Topics to Focus On"
                            icon="📐"
                            topics={missingTopics.math}
                        />

                        <GradientBorderBox
                            title="Reading"
                            score={scores.reading}
                            range="0 - 36"
                            description="Topics to Focus On"
                            icon="📚"
                            topics={missingTopics.reading}
                        />

                        <GradientBorderBox
                            title="Science"
                            score={scores.science}
                            range="0 - 36"
                            description="Topics to Focus On"
                            icon="🔬"
                            topics={missingTopics.science}
                        />
                    </div>
                </div>

                <motion.button
                    whileTap={{ scale: 0.97 }}
                    className="mt-6 bg-gradient-to-r from-[#7aa0ff] to-[#8cb0ff] px-4 py-3 outline-none rounded-xl text-white hover:shadow-lg font-medium w-full transition-all duration-300"
                    onClick={() => navigate('/act')}
                >
                    Back to the Dashboard
                </motion.button>
            </motion.div>
        </div>
    );
};

const GradientBorderBox = ({ title, score, range, description, icon, topics = [] }) => {
    return (
        <div className="relative bg-[#2a2a2a]/80 p-6 rounded-xl shadow-md w-full">
            <div className="absolute inset-0 border-[1px] rounded-xl border-transparent bg-gradient-to-r from-[#5c8dff]/60 to-[#8cb0ff]/60 p-[1px]">
                <div className="h-full w-full bg-[#2a2a2a]/80 rounded-xl"></div>
            </div>
            <div className="relative">
                <span className="inline-block bg-[#3a3a3a] text-[#8cb0ff] text-xs font-semibold px-2 py-1 rounded-full">ACT Section Score</span>
                <h2 className="text-xl font-bold text-white mt-2">{title}</h2>
                <div className="flex items-center mt-4">
                    {icon && <span className="mr-2">{icon}</span>}
                    <span className="text-4xl font-bold text-white">{score}</span>
                    <span className="text-xl text-gray-400 ml-2">{range}</span>
                </div>
                <span className="text-gray-400 text-sm">ACT Score</span>
                <p className="text-[#7aa0ff] font-bold mt-2">{description}</p>

                {/* Missing Topics List */}
                <div className="mt-4 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                    <ul className="list-disc pl-5 text-gray-300">
                        {topics?.map((topic, index) => (
                            <li key={index}>{topic}</li>
                        )) || (
                            <li className="text-gray-400">No missing topics identified</li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ACTScoreboard;